import { turnstileSiteKey } from 'src/config'

const scriptUrl =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

const executeTimeoutMs = 3000

const scriptLoadTimeoutMs = 5000

type Turnstile = {
  render: (el: HTMLElement, options: Record<string, unknown>) => string | null
  execute: (widgetId: string) => void
  reset: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: Turnstile
  }
}

export type CaptchaResult = { token: string } | { error: string }

let scriptPromise: Promise<Turnstile | null> | null = null
let widgetId: string | null = null
let pending: ((result: CaptchaResult) => void) | null = null

const loadScript = () => {
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise<Turnstile | null>((resolve) => {
    // Guarantee settlement even if neither load nor error ever fires,
    // so a hung script fetch can never stall the signup submit.
    const timer = setTimeout(() => resolve(null), scriptLoadTimeoutMs)
    const done = (value: Turnstile | null) => {
      clearTimeout(timer)
      resolve(value)
    }
    const script = document.createElement('script')
    script.src = scriptUrl
    script.async = true
    script.onload = () => done(window.turnstile ?? null)
    script.onerror = () => done(null)
    document.head.appendChild(script)
  })
  return scriptPromise
}

const settle = (result: CaptchaResult) => {
  const resolve = pending
  pending = null
  if (resolve) resolve(result)
}

const ensureWidget = async () => {
  if (!turnstileSiteKey) return false
  const turnstile = await loadScript()
  if (!turnstile) return false
  if (widgetId !== null) return true

  const container = document.createElement('div')
  document.body.appendChild(container)
  try {
    widgetId = turnstile.render(container, {
      sitekey: turnstileSiteKey,
      action: 'signup',
      execution: 'execute',
      callback: (token: string) => settle({ token }),
      'error-callback': (code?: string) => {
        settle({ error: code || 'widget_error' })
        return true
      }
    })
  } catch {
    container.remove()
    return false
  }
  if (widgetId === null) container.remove()
  return widgetId !== null
}

// Load the script and render the invisible widget ahead of time so
// submitting only pays for execute().
export const preloadCaptcha = () => {
  ensureWidget().catch(() => {})
}

export const getCaptchaToken = async (): Promise<CaptchaResult> => {
  const ready = await ensureWidget()
  const turnstile = window.turnstile
  if (!ready || !turnstile || widgetId === null) {
    return { error: 'widget_unavailable' }
  }

  const id = widgetId
  const result = await new Promise<CaptchaResult>((resolve) => {
    const timer = setTimeout(
      () => settle({ error: 'timeout' }),
      executeTimeoutMs
    )
    pending = (r) => {
      clearTimeout(timer)
      resolve(r)
    }
    try {
      turnstile.execute(id)
    } catch {
      settle({ error: 'execute_error' })
    }
  })

  // Tokens are single-use; reset even on failure so the next attempt is fresh.
  try {
    turnstile.reset(id)
  } catch {}

  return result
}
